import { IForm } from 'core-components/classes/models/IForm'
import {
  BaseFetcher,
  ICrudImplementation,
  IDataProviderFilter,
  IDataProviderSort,
  IDataSource,
  IPaging,
  IPagingFromTo,
  IQueryResult,
  Logic,
  Operator,
  TConnectorFetchFn,
  TSyncCache,
} from 'core-components/types'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { Rest } from 'src/core/classes/http'

import { CrudHelper } from './helpers/CrudHelper'
import { catchCreateNotification } from './helpers/notifications/catchCreateNotification'
import { catchDeleteNotification } from './helpers/notifications/catchDeleteNotification'
import { catchUpdateNotification } from './helpers/notifications/catchUpdateNotification'
import { InnerStorageOperationsService } from './innerStorageHelpers/InnerStorageOperationsService'
import { IProcedureFetcherSpec, TSaveToStore } from './interface/IProcedureFetcher'

export class ProcedureInnerStorageFetcher<T, R = string | number>
  extends BaseFetcher<T>
  implements ICrudImplementation<T, R>
{
  public operationsService: InnerStorageOperationsService<T, R>
  public getDataFromStore: () => T[]
  public removeQueries: () => void
  public saveToStore: TSaveToStore<T, R>
  public groupsSort: (sortField: string, keyField: string) => void
  public getDataStore: () => any

  private readonly exportUrl?: string

  constructor(
    public keyField: string,
    private spec: IProcedureFetcherSpec<T>
  ) {
    super()

    this.exportUrl = spec.exportUrl
    this.operationsService = new InnerStorageOperationsService<T, R>(keyField)
    this.getDataFromStore = this.operationsService.getDataFromStore
    this.getDataStore = this.operationsService.getDataStore
    this.removeQueries = this.operationsService.removeQueries
    this.saveToStore = async (query, data, hierarchyField, closeExpandedRoot) => {
      await this.operationsService.saveToStore(query, data, hierarchyField, closeExpandedRoot)
    }
    this.groupsSort = this.operationsService.groupsSort
  }

  public create = async (formId: number | IDataSource, models: IForm[]) => {
    if (this.spec?.createHandler) {
      return this.spec.createHandler({ models })
    }
    return this.makeOperation(models, formId, 'create')
  }

  public update = async (formId: number | IDataSource, models: IForm[]) => {
    if (this.spec?.updateHandler) {
      return this.spec.updateHandler({ models })
    }
    return this.makeOperation(models, formId, 'update')
  }

  public delete = async (
    formId: number | IDataSource,
    value: number | string,
    keyField: string
  ) => {
    if (this.spec?.deleteHandler) {
      return this.spec.deleteHandler({ uids: [value as string] })
    }

    try {
      const crudHelper = this.getCrudHelper(formId)

      if (crudHelper) {
        const result = await crudHelper.delete([{ [keyField]: value as number }], undefined, true)
        this.operationsService.removeFn(this.keyField, value as number)
        return result
      }

      return false
    } catch (e: unknown) {
      catchDeleteNotification(this.getMessageFromError(e))
    }
  }

  public deleteBatch = async (formId: number | IDataSource, values: number[], keyField: string) => {
    try {
      const crudHelper = this.getCrudHelper(formId)

      if (crudHelper) {
        const data = values.map((entryId: number) => ({ [keyField]: entryId }))
        const result = await crudHelper.delete(data, undefined, true)
        this.operationsService.removeBatchFn(this.keyField, values)
        return result
      }

      return false
    } catch (e: unknown) {
      catchDeleteNotification(this.getMessageFromError(e))
    }
  }

  public readById = async (
    id: R,
    keyField: string,
    query: string,
    hierarchyField?: string,
    closeExpandedRoot?: (root: R | null) => void
  ) => {
    const itemFromStore = this.operationsService.findItemInStore(id)
    if (itemFromStore) {
      const resultItem = { items: [itemFromStore], total: 0 }
      if (!this.operationsService.isQueryExistsInDataStore(query)) {
        await this.operationsService.saveToStore(
          query,
          resultItem,
          hierarchyField,
          closeExpandedRoot
        )
      }
      return resultItem
    }
    return { items: [], total: 0 }
  }

  public read = async (
    query: string,
    filter: IDataProviderFilter,
    sort: IDataProviderSort | IDataProviderSort[] | undefined,
    paging: IPaging | IPagingFromTo,
    keyField: string,
    fetchCallBack?: TConnectorFetchFn<T>,
    hierarchyField?: string,
    expanded: (R | null)[] = [],
    root: R | null = null,
    force: boolean = false,
    skipHierarchyFilter: boolean = false,
    orderField?: string,
    closeExpandedRoot?: (root: R | null) => void
  ): Promise<IQueryResult<T, R>> => {
    if (!force) {
      const cache = await this.tryToReadFromCache(
        query,
        filter,
        hierarchyField,
        expanded,
        root,
        skipHierarchyFilter
      )
      if (cache) {
        if (!skipHierarchyFilter && orderField) {
          this.groupsSort(orderField, keyField)
        }
        return cache
      }
    }
    if (this.spec) {
      const storeResult = await this.fetchFromServer(
        query,
        filter,
        sort,
        paging,
        hierarchyField,
        fetchCallBack,
        closeExpandedRoot
      )

      if (!skipHierarchyFilter && orderField) {
        this.operationsService.groupsSort(orderField, keyField)
      }
      let withGroups
      if (!skipHierarchyFilter) {
        const exp = expanded.length ? [...expanded, root] : [root]
        withGroups = await this.operationsService.readFromStore(
          query,
          hierarchyField,
          exp,
          skipHierarchyFilter
        )
      }
      if (skipHierarchyFilter) {
        return storeResult
      }
      if (withGroups) {
        return { ...withGroups, items: withGroups.items || [], error: storeResult.error }
      }
      return {
        items: [],
        total: 0,
        error: storeResult.error,
      }
    }
    return {
      items: [],
      total: 0,
    }
  }

  public searchInCache(keyField: string, keyValue: string): T | undefined {
    return this.operationsService.searchInStore(keyValue, keyField)
  }

  public setAudience(value: boolean) {
    if (this.spec.audience !== value) {
      this.spec.audience = value
      return true
    }
    return false
  }

  public getAudience() {
    return !!this.spec.audience
  }

  public clone(): ProcedureInnerStorageFetcher<T, R> {
    return new ProcedureInnerStorageFetcher(this.keyField, this.spec)
  }

  public updateItem: TSyncCache<T> = ({ model, keyField, hierarchyField }): void => {
    this.operationsService.updateItem({ model, keyField, hierarchyField })
  }

  public deleteItem: TSyncCache<T> = ({ model, keyField }): void => {
    if (model) {
      this.operationsService.deleteItem({ model, keyField })
    }
  }
  public export = async (
    filter: IDataProviderFilter,
    fileName?: string,
    columns?: { title: string; name: string }[]
  ) => {
    if (this.exportUrl) {
      const payload = {
        Format: 'xlsx',
        FileName: fileName || `${new Date().toISOString()}-export`,
        Filter: filter,
        Columns: columns,
      }
      const result = await Rest.post({ url: this.exportUrl, payload })
      if (result) {
        return { Url: '', Blob: result as Blob }
      }
    }
    return { Url: '' }
  }

  private getCrudHelper = (formId: number | IDataSource) => {
    if (typeof formId === 'number' && this.spec.code) {
      return new CrudHelper(formId, this.spec.code)
    } else if (typeof formId !== 'number') {
      return new CrudHelper(parseInt(formId.formId, 10), formId.code)
    }

    catchDeleteNotification('Bad source settings')
    return null
  }

  private fetchFromServer = async (
    query: string,
    filter: IDataProviderFilter,
    sort: IDataProviderSort | IDataProviderSort[] | undefined,
    paging: IPaging | IPagingFromTo,
    hierarchyField?: string,
    fetchCallBack?: TConnectorFetchFn<T>,
    closeExpandedRoot?: (root: R | null) => void
  ) => {
    const crudResult = await this.spec.readFromTo(filter, sort, paging, this.spec.audience)

    const items = fetchCallBack ? await fetchCallBack(crudResult.Data) : crudResult.Data
    const error = crudResult.Errors || ''
    const total = crudResult.Total || items.length

    const storeResult = { items, total, error }

    if (!error) {
      await this.operationsService.saveToStore(
        query,
        storeResult,
        hierarchyField,
        closeExpandedRoot
      )
    }

    return storeResult
  }

  private async makeOperation(
    items: IForm[],
    formId: number | IDataSource,
    action: 'update' | 'create' = 'update'
  ): Promise<T[]> {
    try {
      const crudHelper = this.getCrudHelper(formId)

      if (crudHelper) {
        return await crudHelper[action](
          items.map((el) => ({
            ...el,
            EntryId: el.EntryId || 0,
            AppId: getAppId(),
          })),
          undefined,
          true
        )
      }

      return []
    } catch (e: unknown) {
      const message = this.getMessageFromError(e)
      if (action === 'update') {
        catchUpdateNotification(message)
      } else if (action === 'create') {
        catchCreateNotification(message)
      }
      return []
    }
  }

  private tryToReadFromCache = async (
    query: string,
    filter: IDataProviderFilter,
    hierarchyField?: string,
    expanded: (R | null)[] = [],
    root: R | null = null,
    skipHierarchyFilter: boolean = false
  ): Promise<{
    items: T[]
    total: number
    groups?: T[][]
    unknownRoots?: (R | null)[]
  } | null> => {
    const cache = await this.operationsService.readFromStore(
      query,
      hierarchyField,
      expanded ? [...expanded, root || null] : [root || null],
      skipHierarchyFilter
    )
    if (cache) {
      if (cache.unknownRoots) {
        if (hierarchyField) {
          if (!cache.unknownRoots.length) {
            return { ...cache, items: cache.items || [] }
          }
          filter.filters.push({
            logic: Logic.OR,
            filters: [
              ...cache.unknownRoots.map((value) => {
                return {
                  value: value as string,
                  field: hierarchyField,
                  operator: value === null ? Operator.ISEMPTY : Operator.EQUAL,
                }
              }),
            ],
          })
        } else {
          return { ...cache, items: cache.items || [] }
        }
      } else if (cache.items) {
        return { ...cache, items: cache.items || [] }
      }
    }
    return null
  }
}
